<template>
  <main-container class="model-predict-container" title="モデル適用" :subTitle="modelName">
    <div slot="main-content" style="height: 100%;">
      <form-container>
        <form-item-container label="物件所在地">
          <field-container label="都道府県">
            <span slot="field-item-label">都道府県<span class="required">*</span></span>
            <div>
              <field-select name="pref" v-model="pref" validate="required" validateLabel="都道府県" style="width: 280px;">
                <el-option v-for="prefCity in prefCities" :label="prefCity.prefName"
                           :value="prefCity.prefName"></el-option>
              </field-select>
            </div>
          </field-container>

          <field-container label="市区町村">
            <span slot="field-item-label">市区町村<span class="required">*</span></span>
            <div>
              <field-select name="city" v-model="city" validate="required" validateLabel="市区町村" style="width: 280px;">
                <el-option label="-" value=""></el-option>
                <el-option v-for="cityName in getCities(pref)" :label="cityName" :value="cityName"></el-option>
              </field-select>
            </div>
          </field-container>
        </form-item-container>

        <form-item-container label="建物情報">
          <field-container label="築年月" :class="{ 'has-error': $validator.errors.has('building_ym') }">
            <span slot="field-item-label">築年月<span class="required">*</span></span>
            <div>
              <el-date-picker v-model="buildingYm" name="building_ym" type="month"
                              v-validate="'required|date_format:yyyy/MM'" data-vv-as="築年月"
                              :picker-options="buildingYmPickerOptions" placeholder="2000/01" format="yyyy/MM"
                              value-format="yyyy/MM" style="width: 140px;"></el-date-picker>
              <span class="error-message" v-if="$validator.errors.has('building_ym')"><strong>{{$validator.errors.first('building_ym')}}</strong></span>
            </div>
          </field-container>

          <field-container label="建物種別">
            <span slot="field-item-label">建物種別<span class="required">*</span></span>
            <div>
              <field-select name="building_type" v-model="buildingType" validate="required" validateLabel="建物種別"
                            style="width: 280px;">
                <el-option v-for="buildingTypeOption in BUILDING_TYPE_OPTIONS" :label="buildingTypeOption.label"
                           :value="buildingTypeOption.value"></el-option>
              </field-select>
            </div>
          </field-container>

          <field-container label="建築構造">
            <span slot="field-item-label">建築構造<span class="required">*</span></span>
            <div>
              <field-select name="structure" v-model="structure" validate="required" validateLabel="建築構造"
                            style="width: 280px;">
                <el-option v-for="structureOption in STRUCTURE_OPTIONS" :label="structureOption.label"
                           :value="structureOption.value"></el-option>
              </field-select>
            </div>
          </field-container>

          <field-container label="階数"
                           :class="{ 'has-error': $validator.errors.has('building_total_floor') || $validator.errors.has('underground') }">
            <span slot="field-item-label">階数<span class="required">*</span></span>
            <div>
              <div class="field-item-row">
                <span>地上</span>
                <el-input name="building_total_floor" v-model="buildingTotalFloor" placeholder="1"
                          v-validate="'required|integer|min:1'" data-vv-as="地上階数"
                          style="width: 40px; margin: 0 8px;"></el-input>
                <span>階</span>
                <span style="margin-left: 30px;">地下</span>
                <el-input name="underground" v-model="underground" placeholder="1" v-validate="'integer|min:0'"
                          data-vv-as="地下階数" style="width: 40px; margin: 0 8px;"></el-input>
                <span>階</span>
              </div>
              <span class="error-message" v-if="$validator.errors.has('building_total_floor')"><strong>{{$validator.errors.first('building_total_floor')}}</strong></span>
              <span class="error-message" v-if="$validator.errors.has('underground')"><strong>{{$validator.errors.first('underground')}}</strong></span>
            </div>
          </field-container>

          <field-container label="総戸数" :class="{ 'has-error': $validator.errors.has('num_room') }">
            <span slot="field-item-label">総戸数<span class="required">*</span></span>
            <div>
              <div class="field-item-row">
                <el-input name="num_room" v-model="numRoom" placeholder="1" v-validate="'required|integer|min:1'"
                          data-vv-as="総戸数" style="width: 40px; margin-right: 8px;"></el-input>
                <span>戸</span>
              </div>
              <span class="error-message" v-if="$validator.errors.has('num_room')"><strong>{{$validator.errors.first('num_room')}}</strong></span>
            </div>
          </field-container>

          <field-container label="敷地内駐車場" :class="{ 'has-error': $validator.errors.has('parking_type') }">
            <span slot="field-item-label">敷地内駐車場<span class="required">*</span></span>
            <div>
              <div class="field-item-row">
                <el-radio-group v-model="parkingAvailableProperties">
                  <el-radio name="parking_available_properties" :label="false">なし</el-radio>
                  <el-radio name="parking_available_properties" :label="true">あり</el-radio>
                </el-radio-group>
                <el-select name="parking_type" v-model="parkingType" placeholder="1" v-validate="'required'"
                           data-vv-as="駐車場タイプ" :disabled="!parkingAvailableProperties" style="width: 240px;">
                  <el-option v-for="parkingTypeOption in PARKING_TYPE_OPTIONS" :label="parkingTypeOption.label"
                             :value="parkingTypeOption.value"></el-option>
                </el-select>
              </div>
              <span class="error-message"
                    v-if="parkingAvailableProperties && $validator.errors.has('parking_type')"><strong
                      v-html="$options.filters.html_format($validator.errors.first('parking_type'))"></strong></span>
            </div>
          </field-container>
        </form-item-container>

        <form-item-container label="物件情報">
          <field-container label="所在階" :class="{ 'has-error': $validator.errors.has('floor') }">
            <span slot="field-item-label">所在階<span class="required">*</span></span>
            <div>
              <div class="field-item-row">
                <el-input name="floor" v-model="floor" placeholder="1" v-validate="'required|integer'" data-vv-as="所在階"
                          style="width: 40px; margin-right: 8px;"></el-input>
                <span>階</span>
              </div>
              <span class="error-message" v-if="$validator.errors.has('floor')"><strong>{{$validator.errors.first('floor')}}</strong></span>
            </div>
          </field-container>

          <field-container label="間取り" :class="{ 'has-error': $validator.errors.has('room_pattern') }">
            <span slot="field-item-label">間取り<span class="required">*</span></span>
            <div>
              <div class="field-item-row">
                <el-input name="room_pattern" v-model="roomPattern" placeholder="1LDK"
                          v-validate="'required|room_pattern'" data-vv-as="間取り"
                          style="width: 100px; margin-right: 8px;"></el-input>
              </div>
              <span class="error-message" v-if="$validator.errors.has('room_pattern')"><strong
                      v-html="$options.filters.html_format($validator.errors.first('room_pattern'))"></strong></span>
            </div>
          </field-container>

          <field-container label="方角">
            <span slot="field-item-label">方角<span class="required">*</span></span>
            <div>
              <field-select name="direction" v-model="direction" validate="required" validateLabel="方角"
                            style="width: 70px;">
                <el-option v-for="directionOption in DIRECTION_OPTIONS" :label="directionOption.label"
                           :value="directionOption.value"></el-option>
              </field-select>
            </div>
          </field-container>

          <field-container label="専有面積" :class="{ 'has-error': $validator.errors.has('room_size') }">
            <span slot="field-item-label">専有面積<span class="required">*</span>
              <tooltip-icon>

                <div slot="content">
                  個人的に使用可能な部屋の内側の面積を「専有面積」と言います。<br>
                  バルコニーや玄関ポーチなどの面積は、専有面積に含みません。<br><br>
                  1m<sup>2</sup> ＝ 約0.3坪<br>1坪 ＝ 約3.3m<sup>2</sup><br>1畳(帖) ＝ 約1.6m<sup>2</sup>(江戸間) <br>*1畳(帖)は地域により異なります
                </div>
              </tooltip-icon>
            </span>
            <div>
              <div class="field-item-row">
                <el-input name="room_size" v-model="roomSize" v-validate="'required|decimal:*|min_value:1'"
                          data-vv-as="専有面積" style="width: 100px; margin-right: 8px;"></el-input>
                <span>m<sup>2</sup></span>
              </div>
              <span class="error-message" v-if="$validator.errors.has('room_size')"><strong>{{$validator.errors.first('room_size')}}</strong></span>
            </div>
          </field-container>

          <field-container label="取引態様">
            <span slot="field-item-label">取引態様<span class="required">*</span>
              <tooltip-icon>
                <div slot="content">
                  不動産の取引においての不動産を立場を示すものです。<br><br>
                  貸主：物件所有者が自ら賃貸取引を行います。借主は仲介手数料の負担がありません。<br><br>
                  仲介：仲介業者が物件所有者に変わって賃貸取引を行います。原則として仲介手数料が発生し、借主負担となることが多いです。<br><br>
                  代理：不動産会社などが物件所有者に代わって賃貸取引を行います。原則として仲介手数料が発生しますが、貸主負担となることが多いです。
                </div>
              </tooltip-icon>
            </span>
            <field-radio-group v-model="taiyou" name="rent_pref_taiyou" :options="TAIYO_OPTIONS"></field-radio-group>
          </field-container>
        </form-item-container>

        <form-item-container label="最寄り駅">
          <field-container label="最寄り駅①"
                           :class="{ 'has-error': ($validator.errors.has('line_company_01') || $validator.errors.has('line_name_01') || $validator.errors.has('station_name_01') || $validator.errors.has('walk_01') || $validator.errors.has('bus_01')) }">
            <span slot="field-item-label">最寄り駅①<span class="required">*</span></span>
            <div>
              <div class="field-item-row">
                <span>鉄道</span>
                <el-select name="line_company_01" v-model="lineCompany_01" v-validate="'required'" data-vv-as="鉄道"
                           style="width: 280px; margin: 0 8px;" @change="lineName_01=stationName_01=''">
                  <el-option label="-" value=""></el-option>
                  <el-option v-for="lineCompany in getLineCompanies(pref)" :label="lineCompany.displayCompanyName"
                             :value="lineCompany.displayCompanyName"></el-option>
                </el-select>
              </div>
              <span class="error-message" v-if="$validator.errors.has('line_company_01')"><strong>{{$validator.errors.first('line_company_01')}}</strong></span>

              <div class="field-item-row">
                <span>路線</span>
                <el-select name="line_name_01" v-model="lineName_01" v-validate="'required'" data-vv-as="路線"
                           style="width: 280px; margin: 0 8px;" @change="stationName_01=''">
                  <el-option label="-" value=""></el-option>
                  <el-option v-for="line in getLines(pref, lineCompany_01)" :label="line.lineName"
                             :value="line.lineName"></el-option>
                </el-select>
              </div>
              <span class="error-message" v-if="$validator.errors.has('line_name_01')"><strong>{{$validator.errors.first('line_name_01')}}</strong></span>

              <div class="field-item-row">
                <span>　駅</span>
                <el-select name="station_name_01" v-model="stationName_01" v-validate="'required'" data-vv-as="駅"
                           style="width: 280px; margin: 0 8px;">
                  <el-option label="-" value=""></el-option>
                  <el-option v-for="station in getStations(pref, lineCompany_01, lineName_01)"
                             :label="station.stationName" :value="station.stationName"></el-option>
                </el-select>
                <span>から</span>
              </div>
              <span class="error-message" v-if="$validator.errors.has('station_name_01')"><strong>{{$validator.errors.first('station_name_01')}}</strong></span>

              <div class="field-item-row">
                <span>徒歩</span>
                <el-input name="walk_01" v-model="walk_01" placeholder="5" v-validate="'required|integer|between:1,30'"
                          data-vv-as="徒歩" style="width: 40px; margin: 0 8px;"></el-input>
                <span>分</span>
                <span style="margin-left: 30px">バス</span>
                <el-input name="bus_01" v-model="bus_01" placeholder="0" v-validate="'integer|min_value:0'"
                          data-vv-as="バス" style="width: 40px; margin: 0 8px;"></el-input>
                <span>分</span>
              </div>
              <span class="error-message" v-if="$validator.errors.has('walk_01')"><strong>{{$validator.errors.first('walk_01')}}</strong></span>
              <span class="error-message" v-if="$validator.errors.has('bus_01')"><strong>{{$validator.errors.first('bus_01')}}</strong></span>
            </div>
          </field-container>

          <field-container label="最寄り駅②"
                           :class="{ 'has-error': ($validator.errors.has('walk_02') || $validator.errors.has('bus_02')) }">
            <div>
              <div class="field-item-row">
                <span>鉄道</span>
                <el-select name="line_company_02" v-model="lineCompany_02" style="width: 280px; margin: 0 8px;"
                           @change="lineName_02=stationName_02=''">
                  <el-option label="-" value=""></el-option>
                  <el-option v-for="lineCompany in getLineCompanies(pref)" :label="lineCompany.displayCompanyName"
                             :value="lineCompany.displayCompanyName"></el-option>
                </el-select>
              </div>

              <div class="field-item-row">
                <span>路線</span>
                <el-select name="line_name_02" v-model="lineName_02" style="width: 280px; margin: 0 8px;"
                           @change="stationName_02=''">
                  <el-option label="-" value=""></el-option>
                  <el-option v-for="line in getLines(pref, lineCompany_02)" :label="line.lineName"
                             :value="line.lineName"></el-option>
                </el-select>
              </div>

              <div class="field-item-row">
                <span>　駅</span>
                <el-select name="station_name_02" v-model="stationName_02" style="width: 280px; margin: 0 8px;">
                  <el-option label="-" value=""></el-option>
                  <el-option v-for="station in getStations(pref, lineCompany_02, lineName_02)"
                             :label="station.stationName" :value="station.stationName"></el-option>
                </el-select>
                <span>から</span>
              </div>

              <div class="field-item-row">
                <span>徒歩</span>
                <el-input name="walk_02" v-model="walk_02" v-validate="'integer|between:1,30'" data-vv-as="徒歩"
                          style="width: 40px; margin: 0 8px;"></el-input>
                <span>分</span>
                <span style="margin-left: 30px">バス</span>
                <el-input name="bus_02" v-model="bus_02" v-validate="'integer|min_value:0'" data-vv-as="バス"
                          style="width: 40px; margin: 0 8px;"></el-input>
                <span>分</span>
              </div>
              <span class="error-message" v-if="$validator.errors.has('walk_02')"><strong>{{$validator.errors.first('walk_02')}}</strong></span>
              <span class="error-message" v-if="$validator.errors.has('bus_02')"><strong>{{$validator.errors.first('bus_02')}}</strong></span>
            </div>
          </field-container>

          <field-container label="最寄り駅③"
                           :class="{ 'has-error': ($validator.errors.has('walk_03') || $validator.errors.has('bus_03')) }">
            <div>
              <div class="field-item-row">
                <span>鉄道</span>
                <el-select name="line_company_03" v-model="lineCompany_03" style="width: 280px; margin: 0 8px;"
                           @change="lineName_03=stationName_03=''">
                  <el-option label="-" value=""></el-option>
                  <el-option v-for="lineCompany in getLineCompanies(pref)" :label="lineCompany.displayCompanyName"
                             :value="lineCompany.displayCompanyName"></el-option>
                </el-select>
              </div>

              <div class="field-item-row">
                <span>路線</span>
                <el-select name="line_name_03" v-model="lineName_03" style="width: 280px; margin: 0 8px;"
                           @change="stationName_03=''">
                  <el-option label="-" value=""></el-option>
                  <el-option v-for="line in getLines(pref, lineCompany_03)" :label="line.lineName"
                             :value="line.lineName"></el-option>
                </el-select>
              </div>

              <div class="field-item-row">
                <span>　駅</span>
                <el-select name="station_name_03" v-model="stationName_03" style="width: 280px; margin: 0 8px;">
                  <el-option label="-" value=""></el-option>
                  <el-option v-for="station in getStations(pref, lineCompany_03, lineName_03)"
                             :label="station.stationName" :value="station.stationName"></el-option>
                </el-select>
                <span>から</span>
              </div>

              <div class="field-item-row">
                <span>徒歩</span>
                <el-input name="walk_03" v-model="walk_03" v-validate="'integer|between:1,30'" data-vv-as="徒歩"
                          style="width: 40px; margin: 0 8px;"></el-input>
                <span>分</span>
                <span style="margin-left: 30px">バス</span>
                <el-input name="bus_03" v-model="bus_03" v-validate="'integer|min_value:0'" data-vv-as="バス"
                          style="width: 40px; margin: 0 8px;"></el-input>
                <span>分</span>
              </div>
              <span class="error-message" v-if="$validator.errors.has('walk_03')"><strong>{{$validator.errors.first('walk_03')}}</strong></span>
              <span class="error-message" v-if="$validator.errors.has('bus_03')"><strong>{{$validator.errors.first('bus_03')}}</strong></span>
            </div>
          </field-container>

          <field-container label="最寄り駅④"
                           :class="{ 'has-error': ($validator.errors.has('walk_04') || $validator.errors.has('bus_04')) }">
            <div>
              <div class="field-item-row">
                <span>鉄道</span>
                <el-select name="line_company_04" v-model="lineCompany_04" style="width: 280px; margin: 0 8px;"
                           @change="lineName_04=stationName_04=''">
                  <el-option label="-" value=""></el-option>
                  <el-option v-for="lineCompany in getLineCompanies(pref)" :label="lineCompany.displayCompanyName"
                             :value="lineCompany.displayCompanyName"></el-option>
                </el-select>
              </div>

              <div class="field-item-row">
                <span>路線</span>
                <el-select name="line_name_04" v-model="lineName_04" style="width: 280px; margin: 0 8px;"
                           @change="stationName_04=''">
                  <el-option label="-" value=""></el-option>
                  <el-option v-for="line in getLines(pref, lineCompany_04)" :label="line.lineName"
                             :value="line.lineName"></el-option>
                </el-select>
              </div>

              <div class="field-item-row">
                <span>　駅</span>
                <el-select name="station_name_04" v-model="stationName_04" style="width: 280px; margin: 0 8px;">
                  <el-option label="-" value=""></el-option>
                  <el-option v-for="station in getStations(pref, lineCompany_04, lineName_04)"
                             :label="station.stationName" :value="station.stationName"></el-option>
                </el-select>
                <span>から</span>
              </div>

              <div class="field-item-row">
                <span>徒歩</span>
                <el-input name="walk_04" v-model="walk_04" v-validate="'integer|between:1,30'" data-vv-as="徒歩"
                          style="width: 40px; margin: 0 8px;"></el-input>
                <span>分</span>
                <span style="margin-left: 30px">バス</span>
                <el-input name="bus_04" v-model="bus_04" v-validate="'integer|min_value:0'" data-vv-as="バス"
                          style="width: 40px; margin: 0 8px;"></el-input>
                <span>分</span>
              </div>
              <span class="error-message" v-if="$validator.errors.has('walk_04')"><strong>{{$validator.errors.first('walk_04')}}</strong></span>
              <span class="error-message" v-if="$validator.errors.has('bus_04')"><strong>{{$validator.errors.first('bus_04')}}</strong></span>
            </div>
          </field-container>

          <field-container label="最寄り駅⑤"
                           :class="{ 'has-error': ($validator.errors.has('walk_05') || $validator.errors.has('bus_05')) }">
            <div>
              <div class="field-item-row">
                <span>鉄道</span>
                <el-select name="line_company_05" v-model="lineCompany_05" style="width: 280px; margin: 0 8px;"
                           @change="lineName_05=stationName_05=''">
                  <el-option label="-" value=""></el-option>
                  <el-option v-for="lineCompany in getLineCompanies(pref)" :label="lineCompany.displayCompanyName"
                             :value="lineCompany.displayCompanyName"></el-option>
                </el-select>
              </div>

              <div class="field-item-row">
                <span>路線</span>
                <el-select name="line_name_05" v-model="lineName_05" style="width: 280px; margin: 0 8px;"
                           @change="stationName_05=''">
                  <el-option label="-" value=""></el-option>
                  <el-option v-for="line in getLines(pref, lineCompany_05)" :label="line.lineName"
                             :value="line.lineName"></el-option>
                </el-select>
              </div>

              <div class="field-item-row">
                <span>　駅</span>
                <el-select name="station_name_05" v-model="stationName_05" style="width: 280px; margin: 0 8px;">
                  <el-option label="-" value=""></el-option>
                  <el-option v-for="station in getStations(pref, lineCompany_05, lineName_05)"
                             :label="station.stationName" :value="station.stationName"></el-option>
                </el-select>
                <span>から</span>
              </div>

              <div class="field-item-row">
                <span>徒歩</span>
                <el-input name="walk_05" v-model="walk_05" v-validate="'integer|between:1,30'" data-vv-as="徒歩"
                          style="width: 40px; margin: 0 8px;"></el-input>
                <span>分</span>
                <span style="margin-left: 30px">バス</span>
                <el-input name="bus_05" v-model="bus_05" v-validate="'integer|min_value:0'" data-vv-as="バス"
                          style="width: 40px; margin: 0 8px;"></el-input>
                <span>分</span>
              </div>
              <span class="error-message" v-if="$validator.errors.has('walk_05')"><strong>{{$validator.errors.first('walk_05')}}</strong></span>
              <span class="error-message" v-if="$validator.errors.has('bus_05')"><strong>{{$validator.errors.first('bus_05')}}</strong></span>
            </div>
          </field-container>
        </form-item-container>

        <form-item-container label="その他">
          <field-container>
            <div>
              <el-checkbox v-model="facilities_01">3口以上コンロ</el-checkbox>
              <el-checkbox v-model="facilities_02">CATV</el-checkbox>
              <el-checkbox v-model="facilities_03">TVインターホン</el-checkbox>
              <el-checkbox v-model="facilities_04">ウォークインクローゼット</el-checkbox>
              <el-checkbox v-model="facilities_05">エレベーター</el-checkbox>
              <el-checkbox v-model="facilities_06">オートバス</el-checkbox>
              <el-checkbox v-model="facilities_07">オートロック</el-checkbox>
              <el-checkbox v-model="facilities_08">システムキッチン</el-checkbox>
              <el-checkbox v-model="facilities_09">シャワー付洗面台</el-checkbox>
              <el-checkbox v-model="facilities_10">バストイレ別</el-checkbox>
              <el-checkbox v-model="facilities_19">駅徒歩10分以内</el-checkbox>
              <el-checkbox v-model="facilities_20">駅徒歩5分以内</el-checkbox>
              <el-checkbox v-model="facilities_16">角住戸</el-checkbox>
              <el-checkbox v-model="facilities_14">洗濯機</el-checkbox>
              <el-checkbox v-model="facilities_15">洗面所独立</el-checkbox>
              <el-checkbox v-model="facilities_13">対面式キッチン</el-checkbox>
              <el-checkbox v-model="facilities_12">宅配ボックス</el-checkbox>
              <el-checkbox v-model="facilities_17">追焚機能浴室</el-checkbox>
              <el-checkbox v-model="facilities_18">都市ガス</el-checkbox>
              <el-checkbox v-model="neighboringParkingAvailableProperties">近隣駐車場</el-checkbox>
              <el-checkbox v-model="facilities_11">分譲賃貸</el-checkbox>
            </div>
          </field-container>
        </form-item-container>
      </form-container>

      <el-dialog v-if="predictResult"
                 custom-class="dialog-size-middle"
                 title="家賃推定結果"
                 :top="0"
                 :modal="false"
                 append-to-body="true"
                 :visible.sync="visiblePredictResult"
                 @open="$emit('open')"
                 @close="$emit('close')"
                 @closed="$emit('closed')">
        <div>
          <h3 class="sst_standard">入力いただいた物件情報の家賃推定金額は・・・</h3>
          <p class="rent_pref_result_price">{{getPredictResultPrice() | number_format}}円</p>

          <h3 class="sst_standard">推定範囲</h3>
          <p class="rent_pref_result_price">{{predictResult.remarks.confidenceInterval[0] |
            number_format}}～{{predictResult.remarks.confidenceInterval[1] | number_format}}円</p>
          <p style="text-align: right; font-size: 0.75rem">※金額には管理費が含まれています</p>
        </div>
        <div slot="footer" class="dialog-footer" style="text-align: center;">
          <a :href="predictResult.resultFile">
            <el-button type="info" class="large" style="width: 320px; height: 50px;">結果をダウンロード</el-button>
          </a>
        </div>
      </el-dialog>
    </div>

    <template slot="main-footer">
      <el-button class="large" type="default"
                 @click="changeLocation('model-detail', {modelId: modelId, modelName: modelName}, false, true)">戻る
      </el-button>
      <el-button class="large" type="primary" @click="onClickPredictResultList">適用結果一覧</el-button>
      <el-button class="large" type="info" @click="visiblePredictResult=true" v-show="predictResult != null">結果を表示
      </el-button>
      <el-button class="large" type="primary" @click="onClickPredict">家賃を推定</el-button>
    </template>
  </main-container>
</template>

<script lang="js">
import BaseView from '../../../base'
import PredictResultDialogCmp from '../../../../components/model/predict/dialogs/predict-result.vue'
import WebApiClient from '../../../../api/web'
import { Code } from '../../../../api/base'
import CommonUtil from '../../../../utils/common'
import DateUtil from '../../../../utils/date'
import MessageBox from '../../../../utils/message-box'

export default {
  name: 'RentPrefModelPredictView',
  mixins: [BaseView],
  components: {
    'predict-result-dialog': PredictResultDialogCmp
  },
  props: {
    modelId: ''
  },
  computed: {
    buildingYmPickerOptions (date) {
      return DateUtil.parse('1970/01/01', 'YYYY/MM/DD').isAfter(date)
    },
    TAIYO_OPTIONS () {
      return [
        { label: '貸主', value: '貸主' },
        { label: '仲介', value: '仲介' },
        { label: '代理', value: '代理' }
      ]
    },

    BUILDING_TYPE_OPTIONS () {
      return [
        { label: 'アパート', value: 'アパート' },
        { label: 'テラス・タウンハウス', value: 'テラス・タウンハウス' },
        { label: 'マンション', value: 'マンション' },
        { label: 'その他', value: 'その他' }
      ]
    },

    STRUCTURE_OPTIONS () {
      return [
        { label: '-', value: '' },
        { label: 'ブロック', value: 'ブロック' },
        { label: 'プレコン', value: 'プレコン' },
        { label: '木造', value: '木造' },
        { label: '気泡コン', value: '気泡コン' },
        { label: '軽量鉄骨', value: '軽量鉄骨' },
        { label: '鉄筋コン', value: '鉄筋コン' },
        { label: '鉄骨', value: '鉄骨' },
        { label: '鉄骨プレ', value: '鉄骨プレ' },
        { label: '鉄骨鉄筋', value: '鉄骨鉄筋' }
      ]

    },

    PARKING_TYPE_OPTIONS () {
      return [
        { label: '-', value: '' },
        { label: 'シャッタ付', value: 'シャッタ付' },
        { label: 'シャッタ付地下車庫', value: 'シャッタ付地下車庫' },
        { label: 'シャッタ付車庫', value: 'シャッタ付車庫' },
        { label: 'シャッタ付駐', value: 'シャッタ付駐' },
        { label: 'リモコンシャッタ付地下駐', value: 'リモコンシャッタ付地下駐' },
        { label: 'リモコンシャッタ付駐', value: 'リモコンシャッタ付駐' },
        { label: '地下車庫', value: '地下車庫' },
        { label: '地下駐', value: '地下駐' },
        { label: '屋根付駐', value: '屋根付駐' },
        { label: '平置屋根付駐', value: '平置屋根付駐' },
        { label: '平置駐', value: '平置駐' },
        { label: '機械式駐', value: '機械式駐' },
        { label: '駐2台付', value: '駐2台付' },
        { label: '駐2台可', value: '駐2台可' },
        { label: '駐3台付', value: '駐3台付' },
        { label: '駐3台可', value: '駐3台可' }
      ]
    },

    DIRECTION_OPTIONS () {
      return [
        { label: '北', value: '北' },
        { label: '北東', value: '北東' },
        { label: '北西', value: '北西' },
        { label: '南', value: '南' },
        { label: '南東', value: '南東' },
        { label: '南西', value: '南西' },
        { label: '東', value: '東' },
        { label: '西', value: '西' }
      ]
    }
  },
  data () {
    return {
      modelName: '',
      modelInfo: null,
      predictResult: null,
      visiblePredictResult: false,

      // 都道府県
      pref: '',
      // 市区町村
      city: '',
      // 築年月
      buildingYm: '2000/01',

      // 建物種別
      buildingType: 'マンション',
      // 建築構造
      structure: '',

      // 階数(地上)
      buildingTotalFloor: '',
      // 階数(地下)
      underground: '0',
      // 総戸数
      numRoom: '',

      // 敷地内駐車場
      parkingAvailableProperties: false,
      parkingType: '',

      // 所在階
      floor: '',

      // 間取り
      roomPattern: '',

      // 方角
      direction: '南',

      // 専有面積
      roomSize: '',

      // 取引態様
      taiyou: '貸主',

      // 最寄駅①
      lineCompany_01: '',
      lineName_01: '',
      stationName_01: '',
      walk_01: '',
      bus_01: '0',

      // 最寄駅②
      lineCompany_02: '',
      lineName_02: '',
      stationName_02: '',
      walk_02: '',
      bus_02: '',

      // 最寄駅③
      lineCompany_03: '',
      lineName_03: '',
      stationName_03: '',
      walk_03: '',
      bus_03: '',

      // 最寄駅④
      lineCompany_04: '',
      lineName_04: '',
      stationName_04: '',
      walk_04: '',
      bus_04: '',

      // 最寄駅⑤
      lineCompany_05: '',
      lineName_05: '',
      stationName_05: '',
      walk_05: '',
      bus_05: '',

      // その他
      facilities_01: false,
      facilities_02: false,
      facilities_03: false,
      facilities_04: false,
      facilities_05: false,
      facilities_06: false,
      facilities_07: false,
      facilities_08: false,
      facilities_09: false,
      facilities_10: false,
      facilities_11: false,
      facilities_12: false,
      facilities_13: false,
      facilities_14: false,
      facilities_15: false,
      facilities_16: false,
      facilities_17: false,
      facilities_18: false,
      facilities_19: false,
      facilities_20: false,
      neighboringParkingAvailableProperties: false,

      prefCities: null
    }
  },
  created () {
    WebApiClient.getModel(this, this.modelId).then(response => {
      if (response.data.code === Code.SUCCESS) {
        WebApiClient.getRentPrefCities(this, this.modelId).then((response2) => {
          this.modelInfo = response.data.result
          this.modelName = this.modelInfo.modelName
          this.prefCities = response2.data.result.prefCities
          this.pref = (this.modelId === 'model_rent_pref_kanto') ? '東京都' : '大阪府'
          this.$forceUpdate()
        })
      }
    })
  },
  methods: {
    buildModelPredictParam () {
      return {
        returnInput: 1,
        customOpts: {
          pref: this.pref,
          city: this.city,
          buildingYear: Number(DateUtil.changeFormat(this.buildingYm, 'YYYY/MM', 'YYYY')),
          buildingMonth: Number(DateUtil.changeFormat(this.buildingYm, 'YYYY/MM', 'MM')),
          buildingType: this.buildingType,
          structure: this.structure,
          buildingTotalFloor: this.buildingTotalFloor,
          underground: this.underground,
          numRoom: this.numRoom,
          parkingAvailableProperties: Number(this.parkingAvailableProperties),
          parkingType: this.parkingAvailableProperties ? this.parkingType : '',
          floor: this.floor,
          roomPattern: this.roomPattern,
          direction: this.direction,
          roomSize: this.roomSize,
          taiyou: this.taiyou,

          lineName_01: this.lineName_01,
          stationName_01: this.stationName_01,
          walk_01: String(this.walk_01 || 0),
          bus_01: String(this.bus_01 || 0),

          lineName_02: this.lineName_02,
          stationName_02: this.stationName_02,
          walk_02: String(this.walk_02 || 0),
          bus_02: String(this.bus_02 || 0),

          lineName_03: this.lineName_03,
          stationName_03: this.stationName_03,
          walk_03: String(this.walk_03 || 0),
          bus_03: String(this.bus_03 || 0),

          lineName_04: this.lineName_04,
          stationName_04: this.stationName_04,
          walk_04: String(this.walk_04 || 0),
          bus_04: String(this.bus_04 || 0),

          lineName_05: this.lineName_05,
          stationName_05: this.stationName_05,
          walk_05: String(this.walk_05 || 0),
          bus_05: String(this.bus_05 || 0),

          facilities_01: Number(this.facilities_01),
          facilities_02: Number(this.facilities_02),
          facilities_03: Number(this.facilities_03),
          facilities_04: Number(this.facilities_04),
          facilities_05: Number(this.facilities_05),
          facilities_06: Number(this.facilities_06),
          facilities_07: Number(this.facilities_07),
          facilities_08: Number(this.facilities_08),
          facilities_09: Number(this.facilities_09),
          facilities_10: Number(this.facilities_10),
          facilities_11: Number(this.facilities_11),
          facilities_12: Number(this.facilities_12),
          facilities_13: Number(this.facilities_13),
          facilities_14: Number(this.facilities_14),
          facilities_15: Number(this.facilities_15),
          facilities_16: Number(this.facilities_16),
          facilities_17: Number(this.facilities_17),
          facilities_18: Number(this.facilities_18),
          facilities_19: Number(this.facilities_19),
          facilities_20: Number(this.facilities_20),
          neighboringParkingAvailableProperties: Number(this.neighboringParkingAvailableProperties)
        }
      }
    },

    isValid () {
      return new Promise((resolve) => {
        this.$validator.validateAll().then((result) => {
          resolve(result)
        })
      })
    },

    onClickPredict () {
      this.isValid().then(valid => {
        if (!valid) {
          MessageBox.alert(this, '入力内容に不備があります。<br>エラー内容を確認してください。', {
            dangerouslyUseHTMLString: true
          })
          return
        }

        this.predictResult = null
        this.$forceUpdate()

        WebApiClient.postModelPredict(this, this.modelId, this.buildModelPredictParam()).then((response) => {
          if (response.data.code === Code.SUCCESS) {
            this.predictResult = response.data.result
            this.visiblePredictResult = true
            this.$forceUpdate()
          } else if (response.data.code === Code.TIMEOUT) {
            this.changeLocation('model-predict-result-list', {
              modelId: this.modelId,
              modelName: this.modelName
            }, false, true)
          }
        })
      })
    },

    onClickPredictResultList () {
      this.changeLocation('model-predict-result-list', {
        modelId: this.modelId,
        modelName: this.modelName
      }, false, true)
    },

    getPrefCity (prefName) {
      if (CommonUtil.isNotEmpty(this.prefCities)) {
        for (const prefCity of this.prefCities) {
          if (prefCity.prefName === prefName) {
            return prefCity
          }
        }
      }

      return null
    },

    getCities (prefName) {
      const prefCity = this.getPrefCity(prefName)
      if (CommonUtil.isNotEmpty(prefCity)) {
        return prefCity.cities
      } else {
        return []
      }
    },

    getLineCompanies (prefName) {
      const prefCity = this.getPrefCity(prefName)
      if (CommonUtil.isNotEmpty(prefCity)) {
        return prefCity.lineCompanies
      } else {
        return []
      }
    },

    getLines (prefName, companyName) {
      const lineCompanies = this.getLineCompanies(prefName)
      if (CommonUtil.isNotEmpty(lineCompanies)) {
        for (const lineCompany of lineCompanies) {
          if (lineCompany.displayCompanyName === companyName) {
            return lineCompany.lines
          }
        }
      }
      return []
    },

    getStations (prefName, companyName, lineName) {
      const lines = this.getLines(prefName, companyName)
      if (CommonUtil.isNotEmpty(lines)) {
        for (const line of lines) {
          if (line.lineName === lineName) {
            return line.stations
          }
        }
      }
      return []
    },

    getPredictResultPrice () {
      if (CommonUtil.isEmpty(this.predictResult)) {
        return ''
      }

      const predict = this.predictResult.resultPreview.results[0].value.filter(value => value.name === 'predict')
      return CommonUtil.isNotEmpty(predict) ? predict[0].value : ''
    }
  }
}

</script>

<style scoped>
  .model-predict-container >>> .main-content {
    padding: 24px;
  }

  .required {
    color: #d8243e;
  }

  .field-item-row {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .field-item-row + .field-item-row,
  .error-message + .field-item-row {
    margin-top: 10px;
  }

  .sst_standard {
    font-size: 1.13rem;
    font-weight: normal;
    padding: 40px 0 10px 0;
    border-bottom: 1px solid #6ba7ef;
  }

  .rent_pref_result_price {
    font-size: 1.8rem;
    font-weight: bold;
    text-align: center;
  }
</style>



// WEBPACK FOOTER //
// src/views/model/predict/sx/rent-pref.vue